import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { HelpCircle, RefreshCw } from "lucide-react";
import { useInboxTotals } from "@/lib/inbox";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipProvider } from "@/components/ui/tooltip";
import { PageHeader } from "@/components/ui/page-header";
import { HelpSection } from "@/components/HelpSection";
import { cn } from "@/lib/cn";
import {
  ContentLibraryButton,
  InboxConversationPane,
  InboxDialogList,
  useInboxList,
} from "@/components/inbox";
import { FOLLOW_UP_DUE } from "@/components/inbox/LeadStatus";

/**
 * /inbox — the full triage page ("50 dialogs in a row"). The drawer
 * (InboxDrawer) is the canonical quick-answer surface; both stand on the same
 * list (useInboxList / InboxDialogList) and the same InboxConversationPane, and
 * differ only in layout.
 */
export function InboxPage() {
  const { t } = useTranslation();
  // The dashboard's "time to write" line lands here with that filter on.
  const [openedOnDue] = useState(
    () => new URLSearchParams(window.location.search).get("status") === "due",
  );
  const list = useInboxList({ initialStatus: openedOnDue ? FOLLOW_UP_DUE : null });
  useEffect(() => {
    // The link has done its work. Left in the address, it would put the filter
    // back on every reload after the operator had taken it off.
    if (openedOnDue) window.history.replaceState(null, "", "/inbox");
  }, [openedOnDue]);
  const { selected } = list;
  const [showHelp, setShowHelp] = useState(false);
  // Below lg there is one pane at a time: the list, then (after a tap) the
  // conversation. Stacking both left each ~250px tall. The first dialog is
  // auto-selected for the desktop two-pane view, so "is the conversation open"
  // has to be its own flag.
  const [threadOpen, setThreadOpen] = useState(false);

  // Aggregate counters across the whole fleet (GramGPT shows the same summary).
  // Counted by the server: the rows on screen are what the filters left.
  const totals = useInboxTotals().data;

  return (
    <TooltipProvider delayDuration={300}>
      {/* Below md the shell's top bar (61px) sits above <main>. */}
      <section className="mx-auto flex h-[calc(100dvh-61px)] max-w-7xl flex-col gap-4 px-6 py-6 md:h-[calc(100dvh-1px)]">
        <PageHeader
          title={t("inbox.title")}
          subtitle={t("inbox.subtitle")}
          actions={
            <>
              <Switch
                checked={list.unreadOnly}
                onCheckedChange={(v) => list.setUnreadOnly(!!v)}
                label={t("inbox.unread_only")}
              />
              <ContentLibraryButton
                label
                className="inline-flex h-10 items-center gap-2 rounded-lg px-3 text-sm font-medium text-fg-primary transition-colors hover:bg-bg-elevated/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              />
              <Tooltip content={t("inbox.help.title")}>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => setShowHelp((v) => !v)}
                  aria-label={t("inbox.help.title")}
                  aria-expanded={showHelp}
                >
                  <HelpCircle size={18} strokeWidth={2} aria-hidden="true" />
                </Button>
              </Tooltip>
              <Button
                variant="outline"
                onClick={list.refresh}
                loading={list.refreshing}
                iconLeft={<RefreshCw size={16} strokeWidth={2} aria-hidden="true" />}
              >
                {t("inbox.refresh_now")}
              </Button>
            </>
          }
        />

        <p className="-mt-2 min-h-5 text-sm text-fg-muted">
          {totals && t("inbox.counts", { ...totals })}
          {totals &&
            totals.follow_ups_due > 0 &&
            ` · ${t("inbox.counts_due", { count: totals.follow_ups_due })}`}
        </p>

        {showHelp && (
          <HelpSection i18nKey="inbox.help" paragraphs={0} defaultOpen>
            <ul className="list-disc space-y-1 pl-5 text-sm">
              <li>{t("inbox.help.what_it_shows")}</li>
              <li>{t("inbox.help.polling_cadence")}</li>
              <li>{t("inbox.help.opt_in")}</li>
              <li>{t("inbox.help.reply_path")}</li>
              <li>{t("inbox.help.statuses")}</li>
              <li>{t("inbox.help.search")}</li>
              <li>{t("inbox.help.origin")}</li>
              <li>{t("inbox.help.list_actions")}</li>
              <li>{t("inbox.help.campaign_and_warning")}</li>
              <li>{t("inbox.help.notes")}</li>
              <li>{t("inbox.help.files_and_reactions")}</li>
              <li>{t("inbox.help.library")}</li>
            </ul>
          </HelpSection>
        )}

        {/* A floor on the panes: with the help block open on a low window they
            used to be squeezed to nothing. Now the page scrolls instead. */}
        <div className="grid min-h-[26rem] flex-1 grid-cols-1 gap-3 lg:grid-cols-[320px_1fr]">
          {/* Dialog list */}
          <aside
            className={cn(
              "min-h-0 flex-col overflow-hidden rounded-xl border border-border bg-bg-secondary",
              threadOpen && selected ? "hidden lg:flex" : "flex",
            )}
          >
            <InboxDialogList list={list} onSelect={() => setThreadOpen(true)} />
          </aside>

          {/* Conversation */}
          <section
            className={cn(
              "min-h-0 flex-col overflow-hidden rounded-xl border border-border bg-bg-secondary",
              threadOpen && selected ? "flex" : "hidden lg:flex",
            )}
          >
            {selected ? (
              <InboxConversationPane
                dialog={selected}
                onBack={() => setThreadOpen(false)}
                onArchive={() => list.archiveSelected(() => setThreadOpen(false))}
              />
            ) : (
              <div className="flex flex-1 items-center justify-center p-6 text-sm text-fg-muted">
                {t("inbox.pick_dialog")}
              </div>
            )}
          </section>
        </div>
      </section>
    </TooltipProvider>
  );
}

export default InboxPage;
